// URL utilities for SEO-friendly routes and redirects

// Base62 encoding for shortId generation
const BASE62_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

/**
 * Convert UUID to base62 shortId (8-10 characters)
 */
export function uuidToShortId(uuid: string): string {
  // Remove hyphens and convert to number
  const hex = uuid.replace(/-/g, '');
  let num = BigInt('0x' + hex);
  
  if (num === 0n) return '0';
  
  let result = '';
  while (num > 0n) {
    result = BASE62_CHARS[Number(num % 62n)] + result;
    num = num / 62n;
  }
  
  // Ensure minimum 8 characters
  return result.padStart(8, '0');
}

/**
 * Convert base62 shortId back to UUID
 */
export function shortIdToUuid(shortId: string): string {
  let num = 0n;
  for (let i = 0; i < shortId.length; i++) {
    const char = shortId[i];
    const value = BASE62_CHARS.indexOf(char);
    if (value === -1) throw new Error('Invalid shortId character');
    num = num * 62n + BigInt(value);
  }
  
  // Convert back to hex and format as UUID
  let hex = num.toString(16).padStart(32, '0');
  
  // Ensure valid UUID v4 format by setting version and variant bits
  // Set version to 4 (random UUID)
  hex = hex.slice(0, 12) + '4' + hex.slice(13, 16) + '8' + hex.slice(17);
  
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

/**
 * Slugify title for URL-friendly format
 */
export function slugifyTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim()
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Generate hash for duplicate titles
 */
export function generateTitleHash(title: string, id: string): string {
  const combined = title + id;
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16).slice(0, 4);
}

/**
 * Create SEO-friendly URL for forum posts
 */
export function createForumPostUrl(category: string, id: string, title: string): string {
  const categorySlug = getCategorySlug(category);
  const shortId = isUuid(id) ? uuidToShortId(id) : id;
  let titleSlug = slugifyTitle(title);
  
  // Handle short or duplicate titles
  if (titleSlug.length < 3) {
    titleSlug += '-' + generateTitleHash(title, id);
  }
  
  return `/forum/${categorySlug}/${shortId}-${titleSlug}`;
}

/**
 * Create SEO-friendly URL for creators (slug-only, no UUID)
 */
export function createCreatorUrl(slug: string): string {
  return `/creators/${slug}`;
}

/**
 * Create SEO-friendly URL for dramas (genre+slug format)
 */
export function createDramaUrl(genreSlug: string, titleSlug: string): string {
  return `/dramas/${genreSlug}-${titleSlug}`;
}

/**
 * Create SEO-friendly URL for news articles (slug-only, no UUID)
 */
export function createNewsUrl(id: string | number, title: string, slug?: string): string {
  // If a slug is provided directly, use it
  if (slug) {
    return `/news/${slug}`;
  }
  // Otherwise generate slug from title
  let titleSlug = slugifyTitle(title);
  if (titleSlug.length < 3) {
    titleSlug += '-' + generateTitleHash(title, String(id));
  }
  return `/news/${titleSlug}`;
}

/**
 * Parse SEO URL to extract ID and slug
 */
export function parseUrlParams(urlParam: string): { id: string; slug: string } {
  // Handle simple numeric IDs (backward compatibility)
  if (/^\d+$/.test(urlParam)) {
    return { id: urlParam, slug: 'post' };
  }
  
  // Handle UUID format (backward compatibility)
  if (isUuid(urlParam)) {
    return { id: urlParam, slug: 'post' };
  }
  
  // Handle SEO-friendly format: shortId-slug
  const parts = urlParam.split('-');
  if (parts.length < 2) {
    // If it's not in the expected format, treat the whole thing as an ID
    return { id: urlParam, slug: 'post' };
  }
  
  const shortId = parts[0];
  const slug = parts.slice(1).join('-');
  
  return { id: shortId, slug };
}

/**
 * Convert shortId back to UUID for database queries
 */
export function getIdForDatabase(shortId: string): string {
  // If it's already a UUID, validate and return
  if (isUuid(shortId)) return shortId;
  
  // If it's numeric, return as is (for compatibility)
  if (/^\d+$/.test(shortId)) return shortId;
  
  // If it's a mock ID, return as is
  if (shortId.startsWith('mock-')) return shortId;
  
  // Validate shortId format before conversion
  if (!isShortId(shortId)) {
    throw new Error(`Invalid shortId format: ${shortId}`);
  }
  
  // Convert base62 to UUID
  try {
    const uuid = shortIdToUuid(shortId);
    // Validate the generated UUID
    if (!isUuid(uuid)) {
      throw new Error(`Generated invalid UUID from shortId: ${shortId}`);
    }
    return uuid;
  } catch (error) {
    console.error('Error converting shortId to UUID:', error);
    throw error;
  }
}

/**
 * Check if string is a valid UUID
 */
export function isUuid(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

/**
 * Check if string is a valid shortId
 */
export function isShortId(str: string): boolean {
  return /^[0-9A-Za-z]{8,10}$/.test(str);
}

/**
 * Get category slug from category name
 */
export function getCategorySlug(categoryName: string): string {
  const slugMap: Record<string, string> = {
    'General Discussion': 'general',
    'Reviews': 'reviews',
    'Creators': 'creators',
    'Recommendations': 'recommendations',
    'News': 'news',
    'Behind the Scenes': 'behind-scenes'
  };
  
  return slugMap[categoryName] || slugifyTitle(categoryName);
}

/**
 * Get category name from slug
 */
export function getCategoryFromSlug(slug: string): string {
  const nameMap: Record<string, string> = {
    'general': 'General Discussion',
    'reviews': 'Reviews',
    'creators': 'Creators',
    'recommendations': 'Recommendations',
    'news': 'News',
    'behind-scenes': 'Behind the Scenes'
  };
  
  return nameMap[slug] || slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Legacy URL redirect mappings
 */
export interface RedirectMapping {
  from: string;
  to: string;
  permanent: boolean;
}

/**
 * Generate redirect mappings for legacy URLs
 */
export function generateRedirectMappings(): RedirectMapping[] {
  return [
    // Forum category numeric IDs to new format
    { from: '/forum/reviews/:id', to: '/forum/reviews/:shortId-:slug', permanent: true },
    { from: '/forum/recommendations/:id', to: '/forum/recommendations/:shortId-:slug', permanent: true },
    { from: '/forum/news/:id', to: '/forum/news/:shortId-:slug', permanent: true },
    { from: '/forum/behind-scenes/:id', to: '/forum/behind-scenes/:shortId-:slug', permanent: true },
    { from: '/forum/creators/:id', to: '/forum/creators/:shortId-:slug', permanent: true },
    { from: '/forum/general/:id', to: '/forum/general/:shortId-:slug', permanent: true },
    
    // Old post detail format
    { from: '/post/:uuid', to: '/forum/:category/:shortId-:slug', permanent: true },
    { from: '/forum/post/:id', to: '/forum/:category/:shortId-:slug', permanent: true },
    
    // Creator and drama legacy formats
    { from: '/creators/:id', to: '/creators/:shortId-:slug', permanent: true },
    { from: '/dramas/:id', to: '/dramas/:shortId-:slug', permanent: true }
  ];
}

/**
 * Handle legacy URL redirects
 */
export function handleLegacyRedirect(pathname: string, postData?: any): string | null {
  // Handle /post/:uuid redirects
  const postMatch = pathname.match(/^\/post\/([a-f0-9-]+)$/);
  if (postMatch && postData) {
    const category = getCategorySlug(postData.category || 'general');
    return createForumPostUrl(category, postData.id, postData.title);
  }
  
  // Handle /forum/post/:id redirects
  const forumPostMatch = pathname.match(/^\/forum\/post\/(.+)$/);
  if (forumPostMatch && postData) {
    const category = getCategorySlug(postData.category || 'general');
    return createForumPostUrl(category, postData.id, postData.title);
  }
  
  // Handle numeric forum category URLs
  const categoryMatch = pathname.match(/^\/forum\/([\w-]+)\/(\d+)$/);
  if (categoryMatch && postData) {
    const [, categorySlug, id] = categoryMatch;
    return createForumPostUrl(categorySlug, id, postData.title);
  }
  
  return null;
}

/**
 * Validate and normalize URL parameters
 */
export function validateUrlParams(category: string, urlParam: string): { isValid: boolean; normalizedUrl?: string } {
  try {
    const { id, slug } = parseUrlParams(urlParam);
    
    // Validate category
    const validCategories = ['general', 'reviews', 'creators', 'recommendations', 'news', 'behind-scenes'];
    if (!validCategories.includes(category)) {
      return { isValid: false };
    }
    
    // Validate ID format
    if (!isShortId(id) && !isUuid(id) && !/^\d+$/.test(id)) {
      return { isValid: false };
    }
    
    // Validate slug format
    if (slug.length < 1 || !/^[a-z0-9-]+$/.test(slug)) {
      return { isValid: false };
    }
    
    return { isValid: true, normalizedUrl: `/forum/${category}/${id}-${slug}` };
  } catch {
    return { isValid: false };
  }
}

/**
 * Generate canonical URL for SEO
 */
export function generateCanonicalUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/$/, '')}${path}`;
}

/**
 * Extract metadata for SEO from post data
 */
export function extractSeoMetadata(post: any, category: string) {
  const title = post.title || 'Forum Post';
  const description = post.content ? 
    post.content.substring(0, 160).replace(/\n/g, ' ').trim() + '...' : 
    'Join the discussion on our community forum';
  
  const keywords = [
    category.replace(/-/g, ' '),
    'forum',
    'discussion',
    'community',
    ...(post.tags || [])
  ].slice(0, 5).join(', ');
  
  return {
    title: `${title} - ${getCategoryFromSlug(category)} Forum`,
    description,
    keywords,
    canonical: window.location.href
  };
}